import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logoConfigs, useLogo } from '../contexts/LogoContext';

/**
 * Hook to update PWA icons and favicon based on current route
 * This ensures the app icon changes when installed as PWA
 */
export const usePWAIconUpdate = () => {
  const location = useLocation();
  
  // Always call the hook unconditionally (required by React rules)
  // LogoProvider wraps the app, so this should always be available
  const { logoConfig } = useLogo();

  useEffect(() => {
    // Use logo config from context (includes variant-based config and tournament logo if set)
    // For website, it will have variant-based theme colors but ChessDirector logo
    // For PWA, it can have tournament logos
    const config = logoConfig;

    // Update favicon
    const updateFavicon = (href: string) => {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Update theme color
    const updateThemeColor = (color: string) => {
      let meta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
      }
      meta.content = color;
    };

    // Update apple touch icons
    const updateAppleTouchIcon = (href: string) => {
      // Remove existing apple touch icons
      const existingIcons = document.querySelectorAll("link[rel='apple-touch-icon']");
      existingIcons.forEach(icon => icon.remove());

      // Add new icon
      const link = document.createElement('link');
      link.rel = 'apple-touch-icon';
      link.href = href;
      document.head.appendChild(link);
    };

    // Update document title
    const updateTitle = (name: string) => {
      document.title = `${name} - Chess Tournament`;
    };

    // Fallback when tenant has no logo (avoid empty or Paircraft overwriting org manifest)
    const iconHref = (config.icon && config.icon.trim() !== '') ? config.icon : '/favicon-32.png';

    // Always update favicon, theme, title, and apple-touch-icon
    // Apple-touch-icon is updated for all contexts (not only when PWA installed) so
    // "Add to Home Screen" on tenant apps uses the organization logo.
    updateFavicon(iconHref);
    updateThemeColor(config.themeColor);
    updateTitle(config.name);
    updateAppleTouchIcon(iconHref);
    
    // Update manifest dynamically (even when not installed as PWA)
    // This ensures correct icon when user installs from current page
    const updateManifest = async () => {
      try {
        // Fetch manifest with cache-busting to avoid service worker cache
        const manifestResponse = await fetch(`/manifest.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        const manifest = await manifestResponse.json();

        // If the server already returned a tenant-specific manifest (icons are absolute URLs),
        // do NOT overwrite with the client's blob. The client's logoConfig may still be
        // Paircraft on first render (before /api/tenant/config resolves), which would overwrite
        // the correct org logo with Paircraft.
        const hasServerTenantIcons = manifest.icons?.some(
          (i: { src?: string }) => typeof i?.src === 'string' && (i.src.startsWith('http://') || i.src.startsWith('https://'))
        );
        if (hasServerTenantIcons) {
          return; // Keep server's org manifest; don't replace with blob
        }
        
        manifest.theme_color = config.themeColor;
        manifest.background_color = config.backgroundColor;
        manifest.name = `${config.name} - Chess Tournament`;
        manifest.short_name = config.name;
        
        // Use iconHref so we don't write empty src when tenant has no logo
        const iconSrc = iconHref;
        manifest.icons = [
          {
            src: iconSrc,
            sizes: "192x192",
            type: "image/png",
            purpose: "maskable any"
          },
          {
            src: iconSrc,
            sizes: "512x512",
            type: "image/png",
            purpose: "maskable any"
          },
          {
            src: iconSrc,
            sizes: "64x64 32x32 24x24 16x16",
            type: "image/png"
          }
        ];
        
        // Create blob URL for updated manifest
        const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        // Find or create manifest link
        let manifestLink = document.querySelector("link[rel='manifest']") as HTMLLinkElement;
        if (!manifestLink) {
          manifestLink = document.createElement('link');
          manifestLink.rel = 'manifest';
          document.head.appendChild(manifestLink);
        }
        
        // Clean up old blob URL if it was a blob
        const oldUrl = manifestLink.href;
        if (oldUrl.startsWith('blob:')) {
          URL.revokeObjectURL(oldUrl);
        }
        
        // Update manifest link to blob URL
        manifestLink.href = url;
        
        // Force browser to re-read the manifest by removing and re-adding the link
        // This is necessary for some browsers to pick up the new manifest
        const parent = manifestLink.parentNode;
        if (parent) {
          parent.removeChild(manifestLink);
          setTimeout(() => {
            parent.appendChild(manifestLink);
          }, 0);
        }
      } catch (err) {
        console.error('Error updating manifest:', err);
      }
    };
    
    updateManifest();
  }, [location.pathname, logoConfig]);
};

